import {
  Avatar,
  Button,
  Center,
  Container,
  createStyles,
  Divider,
  Grid,
  Group,
  LoadingOverlay,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconBulldozer, IconTool } from '@tabler/icons'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { Redirect } from '@/components/commons'
import { useFetch } from '@/lib/hooks'
import { Channel as ChannelType, Site as SiteType } from '@/types'

interface Data {
  site?: SiteType
  channel?: ChannelType
}

interface Props {
  initialData?: Data
}

const useStyles = createStyles(theme => ({
  avatar: { borderRadius: '50%' },
  icon: {
    display: 'flex',
    width: '100%',
    height: '100%',
    borderRadius: theme.radius.sm,
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.colors.orange[5],
    backgroundColor: theme.colors.orange[0]
  },
  title: {
    flexGrow: 1,
    fontWeight: 900,
    fontSize: 32,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    [theme.fn.smallerThan('sm')]: { fontSize: theme.fontSizes.xl, textAlign: 'center' }
  },
  text: {
    fontSize: theme.fontSizes.lg,
    [theme.fn.smallerThan('sm')]: { fontSize: theme.fontSizes.md, textAlign: 'center' }
  }
}))

export default function MaintenanceView({ initialData }: Props) {
  // Hooks
  const router = useRouter()
  const { classes } = useStyles()
  const theme = useMantineTheme()
  const isSm = useMediaQuery(`(max-width: ${theme.breakpoints.sm}px)`)

  // Constants
  const { site: siteSlug, channel: channelSlug } = router.query || {}

  // Fetch
  const { data: siteData, error } = useFetch([siteSlug ? `/${siteSlug}/` : null], {
    fallbackData: initialData?.site // rehydration if necessary
  })

  const { data: channelData } = useFetch(
    [siteSlug && channelSlug ? `/${siteSlug}/channels/${channelSlug}/` : null],
    { fallbackData: initialData?.channel } // rehydration if necessary
  )

  // Render
  if (!siteData && !error) return <LoadingOverlay visible />

  if (siteData && siteData.maintenance === false && !channelSlug) {
    return <Redirect url="/" replace />
  }

  if (siteData && siteData.maintenance === true && !!channelSlug) {
    return <Redirect url="/maintenance" replace />
  }

  if (channelData && channelData.maintenance === false) {
    return <Redirect url={`/${channelSlug}`} replace />
  }

  return (
    <Center sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}>
      <Container size="md" p="xl" sx={{ minWidth: '50%' }}>
        <Divider
          my="xl"
          variant="dotted"
          size="sm"
          labelPosition="center"
          label={
            <Avatar
              src={channelData ? channelData.logo : siteData?.logo}
              alt={channelData ? channelData.title : siteData?.seller?.displayName}
              size={60}
              className={classes.avatar}>
              <Skeleton height={60} circle />
            </Avatar>
          }
        />
        <Grid gutter="lg" justify="center">
          <Grid.Col xs={12} sm={8} order={2} orderSm={1}>
            <Stack>
              <Group spacing="xs" position={isSm ? 'center' : 'left'} align="center" noWrap>
                <IconTool size={isSm ? 16 : 24} stroke={2.5} color={theme.colors.gray[7]} />
                <Title className={classes.title}>
                  {channelSlug ? 'Loja' : 'Site'} em manutenção
                </Title>
              </Group>
              <Text
                color="dimmed"
                className={classes.text}
                dangerouslySetInnerHTML={{
                  __html: channelSlug
                    ? channelData?.maintenanceMessage
                    : siteData?.maintenanceMessage
                }}></Text>
              {!!channelSlug && (
                <Button
                  component={Link}
                  href="/"
                  variant="outline"
                  size="lg"
                  hidden={
                    siteData &&
                    siteData.home === 'main-channel' &&
                    siteData.mainChannel?.slug === channelSlug
                  }>
                  Voltar para home
                </Button>
              )}
            </Stack>
          </Grid.Col>
          <Grid.Col xs={12} sm={4} order={1} orderSm={2}>
            <ThemeIcon variant="light" color="orange" className={classes.icon}>
              <IconBulldozer size={isSm ? 72 : 120} stroke={1} />
            </ThemeIcon>
          </Grid.Col>
        </Grid>
        <Divider variant="dotted" size="sm" my="xl" />
      </Container>
    </Center>
  )
}
